import React, {FormEvent, useCallback, useMemo, useState} from "react";
import {Manager, Popper, Reference} from "react-popper";
import {Icon, Toggle, styled, theme, Text} from "@reside/ui";

import {GeneralSearch} from "../search-box";
import {SimpleFacilityModel} from "../../utils/types/facility";

type Props = Readonly<{
  facilities: ReadonlyArray<SimpleFacilityModel>;
  selectedFacilityId: string | null;
  onSelect: (facilityId: string) => void;
}>;

export const FacilitySelect = ({
  facilities,
  selectedFacilityId,
  onSelect,
}: Props) => {
  const [searchValue, setSearchValue] = useState("");
  const searchVisible = facilities.length >= 5;

  const selectedFacility = useMemo(
    () => facilities.find(facility => facility.id === selectedFacilityId),
    [facilities, selectedFacilityId],
  );

  const filteredFacilities = useMemo(
    () =>
      facilities.filter(facility =>
        facility.name.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [facilities, searchValue],
  );

  const handleSelect = useCallback(
    (facilityId: string, setIsOpened: (open: boolean) => void) => {
      onSelect(facilityId);
      setSearchValue("");
      setIsOpened(false);
    },
    [onSelect],
  );

  return (
    <FieldWrapper>
      <Toggle>
        {({isOpened, setIsOpened, outsideClickIgnoreClass}) => (
          <Manager>
            <Reference>
              {({ref}) => (
                <SelectTrigger
                  ref={ref}
                  data-test-id="startWorkflowFacilitySelect"
                  onClick={() => setIsOpened(!isOpened)}
                  type="button"
                >
                  <SelectValue $hasValue={!!selectedFacility}>
                    {selectedFacility?.name ?? "Select a facility"}
                  </SelectValue>
                  <Icon
                    name={isOpened ? "up" : "down"}
                    size={12}
                    color={theme.color.primary}
                  />
                </SelectTrigger>
              )}
            </Reference>
            {isOpened && (
              <Popper placement="bottom-start">
                {({ref, style}) => (
                  <DropdownPanel
                    ref={ref}
                    style={style}
                    className={outsideClickIgnoreClass}
                  >
                    {searchVisible && (
                      <SearchWrapper>
                        <GeneralSearch
                          hideSuggestion
                          isSearching={false}
                          inputValue={searchValue}
                          showClearButton={searchValue.length > 0}
                          onInputChange={(event: FormEvent<HTMLInputElement>) =>
                            setSearchValue(event.currentTarget.value)
                          }
                          onPressSpace={() => {
                            if (searchValue.trim().length > 0) {
                              setSearchValue(`${searchValue} `);
                            }
                          }}
                          placeholder="Search by facility"
                          testId="startWorkflowFacilitySearch"
                          onClearClick={() => setSearchValue("")}
                        />
                      </SearchWrapper>
                    )}
                    <OptionsList>
                      {filteredFacilities.length === 0 ? (
                        <EmptyMessage>No matching facility</EmptyMessage>
                      ) : (
                        filteredFacilities.map(facility => (
                          <OptionButton
                            key={facility.id}
                            data-test-id={`startWorkflowFacilityOption-${facility.id}`}
                            $selected={facility.id === selectedFacilityId}
                            onClick={() =>
                              handleSelect(facility.id, setIsOpened)
                            }
                            type="button"
                          >
                            {facility.name}
                          </OptionButton>
                        ))
                      )}
                    </OptionsList>
                  </DropdownPanel>
                )}
              </Popper>
            )}
          </Manager>
        )}
      </Toggle>
    </FieldWrapper>
  );
};

const FieldWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const SelectTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid ${({theme}) => theme.color.borderGray};
  background: ${({theme}) => theme.color.white};
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: ${({theme}) => theme.color.primary};
  }
`;

const SelectValue = styled(Text)<{$hasValue: boolean}>`
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.25px;
  color: ${({theme, $hasValue}) =>
    $hasValue ? theme.color.darkBlue100 : theme.color.gray100};
  text-align: left;
`;

const DropdownPanel = styled.div`
  margin-top: 8px;
  width: 64%;
  max-height: 320px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({theme}) => theme.color.borderGray};
  border-radius: 4px;
  background: ${({theme}) => theme.color.white};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 1001;
`;

const SearchWrapper = styled.div`
  padding: 12px 12px 0;
`;

const OptionsList = styled.div`
  overflow-y: auto;
  padding: 8px 0 12px;
`;

const OptionButton = styled.button<{$selected: boolean}>`
  font-size: 14px;
  color: ${({theme}) => theme.color.darkBlue100};
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: 0;
  background: ${({theme, $selected}) =>
    $selected ? theme.color.lightBlue10 : "transparent"};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({theme}) => theme.color.lightBlue10};
  }
`;

const EmptyMessage = styled.div`
  padding: 16px;
  text-align: center;
  color: ${({theme}) => theme.color.gray100};
`;
